import { useCallback, useEffect, useRef, useState } from "react";

export type ToastKind = "success" | "error";

export interface ToastAction {
  readonly label: string;
  readonly onClick: () => void;
}

export interface ToastShowOptions {
  readonly kind?: ToastKind;
  readonly action?: ToastAction;
  readonly durationMs?: number;
}

type ToastState = {
  readonly message: string;
  readonly kind: ToastKind;
  readonly action?: ToastAction;
};

export function useToast() {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearToast = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setToast(null);
  }, []);

  const showToast = useCallback(
    (message: string, kindOrOptions: ToastKind | ToastShowOptions = "success") => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
      const options = typeof kindOrOptions === "string" ? { kind: kindOrOptions } : kindOrOptions;
      const kind = options.kind ?? "success";
      const next: ToastState = {
        message,
        kind,
        ...(options.action ? { action: options.action } : {}),
      };
      setToast(next);
      const duration = options.durationMs ?? (options.action ? 12_000 : 2600);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        setToast(null);
      }, duration);
    },
    []
  );

  useEffect(() => clearToast, [clearToast]);

  function Toast() {
    if (!toast) return null;
    return (
      <div className="app-toast-root" role="status" aria-live="polite" aria-atomic="true">
        <div
          className={[
            "app-toast",
            `app-toast--${toast.kind}`,
            toast.action ? "app-toast--with-action" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <span className="app-toast-message">{toast.message}</span>
          {toast.action ? (
            <button
              type="button"
              className="app-toast-action"
              onClick={() => {
                toast.action?.onClick();
                clearToast();
              }}
            >
              {toast.action.label}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return { showToast, clearToast, Toast };
}
